import { useState, useCallback } from "react";
import { siteData, NavPage, CustomSection, CustomSectionType } from "@/store/siteData";
import { uploadFile } from "@/utils/apiClient";

// ── Page Builder: Section Editors ─────────────────────────────────────────────

function HeroEditor({ section, onChange }: { section: Extract<CustomSection, { type: 'hero' }>; onChange: (s: CustomSection) => void }) {
  const [uploading, setUploading] = useState(false);
  const upd = (key: string, val: unknown) => onChange({ ...section, [key]: val });

  const handleImgUpload = async (file: File) => {
    setUploading(true);
    try { const res = await uploadFile(file); upd("imageUrl", res.url); } catch { /* ignore */ }
    setUploading(false);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Başlık</label>
          <input type="text" value={section.title} onChange={e => upd("title", e.target.value)}
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Alt Başlık</label>
          <input type="text" value={section.subtitle} onChange={e => upd("subtitle", e.target.value)}
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
        </div>
      </div>
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Arka Plan Görseli</label>
        <div className="flex gap-2">
          <input type="text" value={section.imageUrl} onChange={e => upd("imageUrl", e.target.value)}
            placeholder="URL veya dosya yükle..."
            className="flex-1 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
          <label className="px-3 py-2 bg-[#3B82F6]/20 border border-[#3B82F6]/40 text-[#A5B5CC] text-sm rounded-lg cursor-pointer hover:bg-[#3B82F6]/30 whitespace-nowrap transition-colors">
            {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
            <input type="file" accept="image/*" className="hidden" disabled={uploading}
              onChange={e => e.target.files?.[0] && handleImgUpload(e.target.files[0])} />
          </label>
        </div>
        {section.imageUrl && (
          <div className="mt-2 relative h-20 rounded-lg overflow-hidden">
            <img src={section.imageUrl} alt="" className="w-full h-full object-cover object-top" />
          </div>
        )}
      </div>
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">
          Karartma: %{Math.round(section.overlayOpacity * 100)}
        </label>
        <input type="range" min="0" max="0.9" step="0.05" value={section.overlayOpacity}
          onChange={e => upd("overlayOpacity", parseFloat(e.target.value))}
          className="w-full h-2 rounded-full accent-[#3B82F6] bg-[#2B3654] cursor-pointer" />
      </div>
    </div>
  );
}

function TextEditor({ section, onChange }: { section: Extract<CustomSection, { type: 'text' }>; onChange: (s: CustomSection) => void }) {
  const upd = (key: string, val: unknown) => onChange({ ...section, [key]: val });
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Başlık</label>
        <input type="text" value={section.heading} onChange={e => upd("heading", e.target.value)}
          className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
      </div>
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">İçerik</label>
        <textarea value={section.body} onChange={e => upd("body", e.target.value)} rows={4}
          className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6] resize-none" />
      </div>
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Hizalama</label>
        <div className="flex gap-2">
          {(['left', 'center', 'right'] as const).map(align => (
            <button key={align} onClick={() => upd("align", align)}
              className={`flex-1 py-2 text-xs rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                section.align === align ? "bg-[#3B82F6]/20 border-[#3B82F6]/60 text-white" : "border-[#2B3654] text-[#7B8FAF] hover:text-white"
              }`}>
              <i className={`${align === 'left' ? 'ri-align-left' : align === 'center' ? 'ri-align-center' : 'ri-align-right'} mr-1`}></i>
              {align === 'left' ? 'Sol' : align === 'center' ? 'Orta' : 'Sağ'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeaturesEditor({ section, onChange }: { section: Extract<CustomSection, { type: 'features' }>; onChange: (s: CustomSection) => void }) {
  const upd = (key: string, val: unknown) => onChange({ ...section, [key]: val });
  const ICONS = ["ri-star-line", "ri-settings-3-line", "ri-shield-check-line", "ri-time-line", "ri-building-2-line", "ri-team-line", "ri-award-line", "ri-heart-line", "ri-lightbulb-line", "ri-rocket-line"];

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Bölüm Başlığı</label>
        <input type="text" value={section.heading} onChange={e => upd("heading", e.target.value)}
          className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-[#7B8FAF] text-xs uppercase tracking-widest">Özellik Kartları</label>
          <button onClick={() => upd("items", [...section.items, { icon: "ri-star-line", title: "Yeni Özellik", desc: "Açıklama" }])}
            className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-xs rounded-lg cursor-pointer whitespace-nowrap hover:bg-emerald-600/30 transition-colors">
            <i className="ri-add-line"></i> Ekle
          </button>
        </div>
        {section.items.map((item, i) => (
          <div key={i} className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <select value={item.icon} onChange={e => {
                const items = [...section.items]; items[i] = { ...items[i], icon: e.target.value }; upd("items", items);
              }} className="bg-[#141E35] border border-[#2B3654] text-white text-xs rounded-lg px-2 py-1.5 outline-none focus:border-[#3B82F6] cursor-pointer">
                {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#3B82F6]/20 flex-shrink-0">
                <i className={`${item.icon} text-[#3B82F6] text-sm`}></i>
              </div>
              <button onClick={() => upd("items", section.items.filter((_, j) => j !== i))}
                className="w-6 h-6 flex items-center justify-center text-red-400 hover:bg-red-500/10 rounded cursor-pointer">
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            </div>
            <input type="text" value={item.title} onChange={e => {
              const items = [...section.items]; items[i] = { ...items[i], title: e.target.value }; upd("items", items);
            }} placeholder="Başlık" className="w-full bg-[#141E35] border border-[#2B3654] text-white text-xs rounded-lg px-2 py-1.5 outline-none focus:border-[#3B82F6]" />
            <input type="text" value={item.desc} onChange={e => {
              const items = [...section.items]; items[i] = { ...items[i], desc: e.target.value }; upd("items", items);
            }} placeholder="Açıklama" className="w-full bg-[#141E35] border border-[#2B3654] text-white text-xs rounded-lg px-2 py-1.5 outline-none focus:border-[#3B82F6]" />
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsEditor({ section, onChange }: { section: Extract<CustomSection, { type: 'stats' }>; onChange: (s: CustomSection) => void }) {
  const upd = (key: string, val: unknown) => onChange({ ...section, [key]: val });
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Bölüm Başlığı</label>
        <input type="text" value={section.heading} onChange={e => upd("heading", e.target.value)}
          className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-[#7B8FAF] text-xs uppercase tracking-widest">İstatistikler</label>
          <button onClick={() => upd("items", [...section.items, { value: "100+", label: "Yeni İstatistik" }])}
            className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-xs rounded-lg cursor-pointer whitespace-nowrap hover:bg-emerald-600/30 transition-colors">
            <i className="ri-add-line"></i> Ekle
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {section.items.map((item, i) => (
            <div key={i} className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-2.5 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[#5E7398] text-xs">#{i + 1}</span>
                <button onClick={() => upd("items", section.items.filter((_, j) => j !== i))}
                  className="w-5 h-5 flex items-center justify-center text-red-400 hover:bg-red-500/10 rounded cursor-pointer">
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
              <input type="text" value={item.value} onChange={e => {
                const items = [...section.items]; items[i] = { ...items[i], value: e.target.value }; upd("items", items);
              }} placeholder="1.000+" className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm font-bold rounded-lg px-2 py-1.5 outline-none focus:border-[#3B82F6] text-center" />
              <input type="text" value={item.label} onChange={e => {
                const items = [...section.items]; items[i] = { ...items[i], label: e.target.value }; upd("items", items);
              }} placeholder="Etiket" className="w-full bg-[#141E35] border border-[#2B3654] text-[#7B8FAF] text-xs rounded-lg px-2 py-1.5 outline-none focus:border-[#3B82F6] text-center" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CTAEditor({ section, onChange }: { section: Extract<CustomSection, { type: 'cta' }>; onChange: (s: CustomSection) => void }) {
  const upd = (key: string, val: unknown) => onChange({ ...section, [key]: val });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Başlık</label>
          <input type="text" value={section.heading} onChange={e => upd("heading", e.target.value)}
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Alt Başlık</label>
          <input type="text" value={section.subtitle} onChange={e => upd("subtitle", e.target.value)}
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Buton Metni</label>
          <input type="text" value={section.buttonText} onChange={e => upd("buttonText", e.target.value)}
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Buton Linki</label>
          <input type="text" value={section.buttonLink} onChange={e => upd("buttonLink", e.target.value)}
            placeholder="/iletisim veya https://..."
            className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
        </div>
      </div>
    </div>
  );
}

// ── Section Type Info ─────────────────────────────────────────────────────────

const SECTION_TYPES: { type: CustomSectionType; label: string; icon: string; desc: string }[] = [
  { type: 'hero',     label: 'Hero Bölümü',        icon: 'ri-image-2-line',        desc: 'Büyük arka plan görseli + başlık' },
  { type: 'text',     label: 'Metin Bölümü',        icon: 'ri-article-line',         desc: 'Başlık ve paragraf içeriği' },
  { type: 'features', label: 'Özellik Kartları',    icon: 'ri-layout-grid-line',     desc: 'İkon + başlık + açıklama kartları' },
  { type: 'stats',    label: 'İstatistikler',       icon: 'ri-bar-chart-2-line',     desc: 'Sayısal değer göstergeleri' },
  { type: 'cta',      label: 'Eylem Çağrısı',      icon: 'ri-cursor-line',          desc: 'Buton ve yönlendirme alanı' },
];

function createSection(type: CustomSectionType): CustomSection {
  const id = `${type}-${Date.now()}`;
  switch (type) {
    case 'hero':     return { type, id, title: 'Sayfa Başlığı', subtitle: 'Alt başlık', imageUrl: '', overlayOpacity: 0.5 };
    case 'text':     return { type, id, heading: 'Bölüm Başlığı', body: 'İçerik buraya...', align: 'left' };
    case 'features': return { type, id, heading: 'Özelliklerimiz', items: [{ icon: 'ri-star-line', title: 'Özellik 1', desc: 'Açıklama' }] };
    case 'stats':    return { type, id, heading: 'Rakamlarla Biz', items: [{ value: '100+', label: 'Proje' }] };
    case 'cta':      return { type, id, heading: 'Hemen Başlayın', subtitle: 'Bugün iletişime geçin', buttonText: 'İletişim', buttonLink: '/iletisim' };
    default:         return { type: 'text', id, heading: '', body: '', align: 'left' };
  }
}

// ── Page Editor ───────────────────────────────────────────────────────────────

function PageEditor({ page, onSave, onBack }: { page: NavPage; onSave: (p: NavPage) => void; onBack: () => void }) {
  const [editPage, setEditPage] = useState<NavPage>(() => ({ ...page, sections: page.sections ?? [] }));
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [saved, setSaved] = useState(false);

  const sections = editPage.sections ?? [];

  const handleSave = () => {
    onSave(editPage);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const addSection = (type: CustomSectionType) => {
    const newSec = createSection(type);
    setEditPage(p => ({ ...p, sections: [...(p.sections ?? []), newSec] }));
    setExpandedSection(newSec.id);
    setShowAddSection(false);
  };

  const updateSection = useCallback((updated: CustomSection) => {
    setEditPage(p => ({ ...p, sections: (p.sections ?? []).map(s => s.id === updated.id ? updated : s) }));
  }, []);

  const removeSection = (id: string) => {
    setEditPage(p => ({ ...p, sections: (p.sections ?? []).filter(s => s.id !== id) }));
    if (expandedSection === id) setExpandedSection(null);
  };

  const moveSection = (id: string, dir: 'up' | 'down') => {
    setEditPage(p => {
      const secs = [...(p.sections ?? [])];
      const idx = secs.findIndex(s => s.id === id);
      if (dir === 'up' && idx > 0) [secs[idx - 1], secs[idx]] = [secs[idx], secs[idx - 1]];
      if (dir === 'down' && idx < secs.length - 1) [secs[idx], secs[idx + 1]] = [secs[idx + 1], secs[idx]];
      return { ...p, sections: secs };
    });
  };

  const SECTION_LABELS: Record<string, string> = {
    hero: 'Hero Bölümü', text: 'Metin Bölümü', features: 'Özellik Kartları', stats: 'İstatistikler', cta: 'Eylem Çağrısı',
  };
  const SECTION_ICONS: Record<string, string> = {
    hero: 'ri-image-2-line', text: 'ri-article-line', features: 'ri-layout-grid-line', stats: 'ri-bar-chart-2-line', cta: 'ri-cursor-line',
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack}
          className="flex items-center gap-1.5 text-[#7B8FAF] hover:text-white text-sm cursor-pointer transition-colors">
          <i className="ri-arrow-left-line"></i> Geri
        </button>
        <div className="flex-1">
          <h2 className="text-white font-semibold text-base">{page.builtIn ? page.label : 'Sayfa Düzenleniyor'}</h2>
          <p className="text-[#5E7398] text-xs">{editPage.slug}</p>
        </div>
        <button onClick={handleSave}
          className={`px-4 py-2 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all flex items-center gap-2 ${
            saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"
          }`}>
          {saved ? <><i className="ri-check-line"></i> Kaydedildi!</> : <><i className="ri-save-line"></i> Kaydet</>}
        </button>
      </div>

      {/* Sayfa Bilgileri */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-5 space-y-4">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest font-semibold">Sayfa Bilgileri</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Sayfa Adı (Navbar'da görünür)</label>
            <input type="text" value={editPage.label}
              onChange={e => setEditPage(p => ({ ...p, label: e.target.value }))}
              className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]" />
          </div>
          {!page.builtIn && (
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">URL Kısmı (slug)</label>
              <div className="flex items-center bg-[#0F1729] border border-[#2B3654] rounded-lg overflow-hidden focus-within:border-[#3B82F6] transition-colors">
                <span className="px-3 text-[#5E7398] text-sm border-r border-[#2B3654] py-2.5 whitespace-nowrap">/sayfa/</span>
                <input type="text" value={editPage.slug.replace('/sayfa/', '')}
                  onChange={e => {
                    const val = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                    setEditPage(p => ({ ...p, slug: `/sayfa/${val}` }));
                  }}
                  className="flex-1 bg-transparent text-white text-sm px-3 py-2.5 outline-none" />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between bg-[#0F1729] border border-[#2B3654] rounded-xl px-4 py-3">
          <div>
            <p className="text-white text-sm font-medium">Navbar'da Gizle</p>
            <p className="text-[#5E7398] text-xs mt-0.5">Sayfaya link ile erişilebilir ama menüde görünmez</p>
          </div>
          <button onClick={() => setEditPage(p => ({ ...p, hiddenFromNav: !p.hiddenFromNav }))}
            className={`relative w-11 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 ${editPage.hiddenFromNav ? "bg-red-500" : "bg-emerald-500"}`}>
            <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all" style={{ left: editPage.hiddenFromNav ? "22px" : "2px" }}></span>
          </button>
        </div>
      </div>

      {/* Bölümler - sadece özel sayfalar için */}
      {!page.builtIn && (
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-semibold text-sm">Sayfa Bölümleri</p>
              <p className="text-[#5E7398] text-xs mt-0.5">{sections.length} bölüm · Sıralarını ayarlayabilirsiniz</p>
            </div>
            <button onClick={() => setShowAddSection(p => !p)}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#3B82F6]/20 border border-[#3B82F6]/40 text-[#A5B5CC] text-sm rounded-lg cursor-pointer hover:bg-[#3B82F6]/30 transition-colors whitespace-nowrap">
              <i className={showAddSection ? "ri-close-line" : "ri-add-line"}></i>
              {showAddSection ? "İptal" : "Bölüm Ekle"}
            </button>
          </div>

          {/* Add Section Panel */}
          {showAddSection && (
            <div className="bg-[#0A1020] border border-[#3B82F6]/30 rounded-xl p-4">
              <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-3">Eklenecek Bölüm Türü</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {SECTION_TYPES.map(st => (
                  <button key={st.type} onClick={() => addSection(st.type)}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-[#0F1729] border border-[#2B3654] hover:border-[#3B82F6]/50 cursor-pointer transition-all text-center group">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#3B82F6]/20 group-hover:bg-[#3B82F6]/30 transition-colors">
                      <i className={`${st.icon} text-[#3B82F6] text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-white text-xs font-medium">{st.label}</p>
                      <p className="text-[#5E7398] text-[10px] mt-0.5 leading-tight">{st.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Section List */}
          {sections.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed border-[#2B3654] rounded-xl">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0F1729] mx-auto mb-3">
                <i className="ri-layout-3-line text-2xl text-[#3B82F6]"></i>
              </div>
              <p className="text-white text-sm font-medium">Henüz bölüm eklenmedi</p>
              <p className="text-[#5E7398] text-xs mt-1">"Bölüm Ekle" butonuna tıklayın</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sections.map((sec, idx) => (
                <div key={sec.id} className="bg-[#0F1729] border border-[#2B3654] rounded-xl overflow-hidden">
                  {/* Section header */}
                  <div
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setExpandedSection(expandedSection === sec.id ? null : sec.id)}
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#141E35] flex-shrink-0">
                      <i className={`${SECTION_ICONS[sec.type] ?? 'ri-layout-line'} text-[#3B82F6] text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{SECTION_LABELS[sec.type] ?? sec.type}</p>
                      <p className="text-[#5E7398] text-xs">
                        {sec.type === 'hero' ? (sec.title || 'Başlıksız') :
                         sec.type === 'text' ? (sec.heading || 'Başlıksız') :
                         sec.type === 'features' ? `${sec.items.length} kart` :
                         sec.type === 'stats' ? `${sec.items.length} istatistik` :
                         sec.type === 'cta' ? (sec.heading || 'Başlıksız') : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={e => { e.stopPropagation(); moveSection(sec.id, 'up'); }}
                        disabled={idx === 0}
                        className="w-6 h-6 flex items-center justify-center text-[#7B8FAF] hover:text-white rounded disabled:opacity-30 cursor-pointer hover:bg-white/5 transition-colors">
                        <i className="ri-arrow-up-s-line text-sm"></i>
                      </button>
                      <button onClick={e => { e.stopPropagation(); moveSection(sec.id, 'down'); }}
                        disabled={idx === sections.length - 1}
                        className="w-6 h-6 flex items-center justify-center text-[#7B8FAF] hover:text-white rounded disabled:opacity-30 cursor-pointer hover:bg-white/5 transition-colors">
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </button>
                      <button onClick={e => { e.stopPropagation(); removeSection(sec.id); }}
                        className="w-6 h-6 flex items-center justify-center text-[#7B8FAF] hover:text-red-400 rounded cursor-pointer hover:bg-red-500/10 transition-colors">
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                      <i className={`${expandedSection === sec.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#3B82F6] text-sm ml-1`}></i>
                    </div>
                  </div>
                  {/* Section editor */}
                  {expandedSection === sec.id && (
                    <div className="px-4 pb-4 border-t border-[#2B3654] pt-4">
                      {sec.type === 'hero'     && <HeroEditor section={sec} onChange={updateSection} />}
                      {sec.type === 'text'     && <TextEditor section={sec} onChange={updateSection} />}
                      {sec.type === 'features' && <FeaturesEditor section={sec} onChange={updateSection} />}
                      {sec.type === 'stats'    && <StatsEditor section={sec} onChange={updateSection} />}
                      {sec.type === 'cta'      && <CTAEditor section={sec} onChange={updateSection} />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Built-in note */}
      {page.builtIn && (
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl px-4 py-3 flex items-start gap-3">
          <i className="ri-information-line text-[#3B82F6] text-sm flex-shrink-0 mt-0.5"></i>
          <p className="text-[#7B8FAF] text-xs leading-relaxed">
            Bu sayfa <strong className="text-white">yerleşik</strong> bir sayfa. İçeriği Kontrol Paneli'ndeki ilgili editörden düzenleyebilirsiniz.
            Burada yalnızca navbar görünürlüğü ve menü adını değiştirebilirsiniz.
          </p>
        </div>
      )}

      <button onClick={handleSave}
        className={`w-full py-3 text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 ${
          saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"
        }`}>
        {saved ? <><i className="ri-check-double-line"></i> Kaydedildi!</> : <><i className="ri-save-line"></i> Değişiklikleri Kaydet</>}
      </button>
    </div>
  );
}

// ── Main Navigation Manager ──────────────────────────────────────────────────

export default function NavigationManager() {
  const [pages, setPages] = useState<NavPage[]>(() => siteData.getNavPages());
  const [editingPage, setEditingPage] = useState<NavPage | null>(null);
  const [showNewPage, setShowNewPage] = useState(false);
  const [newPageLabel, setNewPageLabel] = useState("");
  const [newPageSlug, setNewPageSlug] = useState("");
  const [saved, setSaved] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleSavePages = async (updated: NavPage[]) => {
    setPages(updated);
    await siteData.saveNavPages(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleToggleNav = async (id: string) => {
    const updated = pages.map(p => p.id === id ? { ...p, hiddenFromNav: !p.hiddenFromNav } : p);
    await handleSavePages(updated);
  };

  const handleAddPage = async () => {
    if (!newPageLabel.trim()) return;
    const slug = newPageSlug.trim() || newPageLabel.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const newPage: NavPage = {
      id: `custom-${Date.now()}`,
      slug: `/sayfa/${slug}`,
      label: newPageLabel.trim(),
      hiddenFromNav: false,
      builtIn: false,
      sections: [],
    };
    const updated = [...pages, newPage];
    await handleSavePages(updated);
    setShowNewPage(false);
    setNewPageLabel("");
    setNewPageSlug("");
  };

  const handleDeletePage = async (id: string) => {
    if (deletingId !== id) { setDeletingId(id); return; }
    const updated = pages.filter(p => p.id !== id);
    await handleSavePages(updated);
    setDeletingId(null);
  };

  const handleSavePage = async (updated: NavPage) => {
    const updatedPages = pages.map(p => p.id === updated.id ? updated : p);
    await handleSavePages(updatedPages);
  };

  const movePage = async (id: string, dir: 'up' | 'down') => {
    const arr = [...pages];
    const idx = arr.findIndex(p => p.id === id);
    if (dir === 'up' && idx > 0) [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    if (dir === 'down' && idx < arr.length - 1) [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    await handleSavePages(arr);
  };

  if (editingPage) {
    return (
      <PageEditor
        page={editingPage}
        onSave={async (updated) => { await handleSavePage(updated); setEditingPage(null); }}
        onBack={() => setEditingPage(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Sayfa Yönetimi</h1>
          <p className="text-[#7B8FAF] text-sm mt-0.5">Sayfa ekle, çıkar, gizle ve sırala</p>
        </div>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="flex items-center gap-1.5 text-emerald-400 text-xs">
              <i className="ri-check-double-line"></i> Kaydedildi
            </span>
          )}
          <button onClick={() => setShowNewPage(p => !p)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all ${
              showNewPage ? "bg-[#2B3654] text-[#7B8FAF]" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"
            }`}>
            <i className={showNewPage ? "ri-close-line" : "ri-add-line"}></i>
            {showNewPage ? "İptal" : "Yeni Sayfa Ekle"}
          </button>
        </div>
      </div>

      {/* New Page Form */}
      {showNewPage && (
        <div className="bg-[#141E35] border border-[#3B82F6]/40 rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B82F6]/20 flex-shrink-0">
              <i className="ri-add-circle-line text-[#3B82F6] text-xl"></i>
            </div>
            <div>
              <p className="text-white font-semibold">Yeni Özel Sayfa</p>
              <p className="text-[#7B8FAF] text-xs">Sayfayı oluşturduktan sonra içeriğini düzenleyebilirsiniz</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Sayfa Adı</label>
              <input type="text" value={newPageLabel} onChange={e => setNewPageLabel(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAddPage()}
                placeholder="örn: Hizmetlerimiz"
                className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6]" />
            </div>
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">URL Slug (opsiyonel)</label>
              <div className="flex items-center bg-[#0F1729] border border-[#2B3654] rounded-lg overflow-hidden focus-within:border-[#3B82F6]">
                <span className="px-3 text-[#5E7398] text-xs border-r border-[#2B3654] py-2.5 whitespace-nowrap">/sayfa/</span>
                <input type="text" value={newPageSlug} onChange={e => setNewPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                  placeholder="otomatik"
                  className="flex-1 bg-transparent text-white text-sm px-3 py-2.5 outline-none" />
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={handleAddPage} disabled={!newPageLabel.trim()}
              className="flex-1 py-2.5 bg-[#3B82F6] hover:bg-[#3d4e6a] text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              <i className="ri-add-line"></i> Sayfayı Oluştur
            </button>
            <button onClick={() => setShowNewPage(false)}
              className="px-4 py-2.5 border border-[#2B3654] text-[#7B8FAF] text-sm rounded-lg hover:text-white cursor-pointer whitespace-nowrap transition-colors">
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Pages List */}
      <div className="space-y-3">
        {/* Header row */}
        <div className="grid grid-cols-12 gap-3 px-4 pb-2 border-b border-[#2B3654]">
          <div className="col-span-5 text-[#5E7398] text-xs uppercase tracking-widest">Sayfa</div>
          <div className="col-span-3 text-[#5E7398] text-xs uppercase tracking-widest">URL</div>
          <div className="col-span-2 text-[#5E7398] text-xs uppercase tracking-widest text-center">Navbar</div>
          <div className="col-span-2 text-[#5E7398] text-xs uppercase tracking-widest text-right">İşlemler</div>
        </div>

        {pages.map((page, idx) => (
          <div key={page.id}
            className={`grid grid-cols-12 gap-3 items-center px-4 py-3 rounded-xl border transition-all ${
              page.hiddenFromNav ? "border-[#2B3654]/50 bg-[#0F1729]/50 opacity-70" : "border-[#2B3654] bg-[#0F1729]"
            }`}>
            {/* Page info */}
            <div className="col-span-5 flex items-center gap-3 min-w-0">
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button onClick={() => movePage(page.id, 'up')} disabled={idx === 0}
                  className="w-5 h-5 flex items-center justify-center text-[#5E7398] hover:text-white rounded disabled:opacity-25 cursor-pointer transition-colors">
                  <i className="ri-arrow-up-s-line text-sm"></i>
                </button>
                <button onClick={() => movePage(page.id, 'down')} disabled={idx === pages.length - 1}
                  className="w-5 h-5 flex items-center justify-center text-[#5E7398] hover:text-white rounded disabled:opacity-25 cursor-pointer transition-colors">
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
              </div>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#141E35] flex-shrink-0">
                <i className={`${page.builtIn ? 'ri-pages-line' : 'ri-file-text-line'} text-[#3B82F6] text-sm`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">{page.label}</p>
                {page.builtIn && (
                  <span className="text-[#465886] text-[10px]">Yerleşik sayfa</span>
                )}
              </div>
            </div>
            {/* URL */}
            <div className="col-span-3 min-w-0">
              <p className="text-[#5E7398] text-xs font-mono truncate">{page.slug}</p>
            </div>
            {/* Navbar toggle */}
            <div className="col-span-2 flex justify-center">
              <button onClick={() => handleToggleNav(page.id)}
                className={`relative w-10 h-5 rounded-full transition-all cursor-pointer ${page.hiddenFromNav ? "bg-[#2B3654]" : "bg-emerald-500"}`}>
                <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow"
                  style={{ left: page.hiddenFromNav ? '2px' : '22px' }}></span>
              </button>
            </div>
            {/* Actions */}
            <div className="col-span-2 flex items-center justify-end gap-1.5">
              <button onClick={() => setEditingPage(page)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-[#3B82F6]/15 text-[#A5B5CC] text-xs rounded-lg hover:bg-[#3B82F6]/30 cursor-pointer whitespace-nowrap transition-colors border border-[#3B82F6]/20">
                <i className="ri-edit-line text-xs"></i>
                <span className="hidden sm:inline">Düzenle</span>
              </button>
              {!page.builtIn && (
                deletingId === page.id ? (
                  <div className="flex gap-1">
                    <button onClick={() => handleDeletePage(page.id)}
                      className="px-2 py-1.5 bg-red-500/20 text-red-400 text-xs rounded-lg cursor-pointer border border-red-500/30 whitespace-nowrap hover:bg-red-500/30 transition-colors">
                      Evet
                    </button>
                    <button onClick={() => setDeletingId(null)}
                      className="px-2 py-1.5 text-[#7B8FAF] text-xs border border-[#2B3654] rounded-lg cursor-pointer whitespace-nowrap hover:text-white transition-colors">
                      İptal
                    </button>
                  </div>
                ) : (
                  <button onClick={() => handleDeletePage(page.id)}
                    className="w-7 h-7 flex items-center justify-center text-[#5E7398] hover:text-red-400 hover:bg-red-500/10 rounded-lg cursor-pointer transition-colors">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl px-4 py-3 space-y-2">
        <div className="flex items-center gap-2">
          <i className="ri-information-line text-[#3B82F6] text-sm"></i>
          <p className="text-white text-xs font-semibold">Nasıl Çalışır?</p>
        </div>
        <ul className="space-y-1 pl-2">
          {[
            "Navbar göster/gizle — Ziyaretçilerin üst menüde görüp görmeyeceğini kontrol eder",
            "Sıralama okları — Sayfaların navbar'da görünme sırasını belirler",
            "Düzenle — Sayfa adını, URL'yi ve bölümlerini değiştirin",
            "Yeni Sayfa — İçerikleri sekmelerle kolayca oluşturabileceğiniz özel sayfa ekleyin",
          ].map((tip, i) => (
            <li key={i} className="flex items-start gap-2 text-[#5E7398] text-xs">
              <i className="ri-checkbox-blank-circle-fill text-[5px] text-[#3B82F6] mt-1.5 flex-shrink-0"></i>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
